import { Module, MutationTree, GetterTree, ActionTree } from 'vuex';
import JwtDecode from 'jwt-decode';
import { AuthState, RootState } from '../types/store';
import AuthService from '@/auth';
import { getItem, setItem, removeItem } from '@/utils/storage';

const readToken = () => {
  const token = getItem(TOKEN);
  if (!token) {
    return '';
  } else if (isValidToken(token) && !tokenHasExpired(token)) {
    return token;
  } else {
    removeItem(TOKEN);
    return '';
  }
};

const tokenHasExpired = (token: string) => {
  const decodedToken: any = JwtDecode(token);
  const exp = decodedToken.exp * 1000;
  return new Date(exp) < new Date();
};

const isValidToken = (token: string) => {
  try {
    JwtDecode(token, { header: true });
    return true;
  } catch (error) {
    return false;
  }
};

const authService = AuthService;

// Mutations
const SET_TOKEN = 'setToken';

// Actions
const LOGIN = 'login';
const HANDLE_AUTHENTICATION = 'handleAuthentication';
const LOGOUT = 'logout';

// Getters
const TOKEN = 'token';
const IS_LOGGED_IN = 'isLoggedIn';

const state: AuthState = {
  token: readToken()
};

const mutations: MutationTree<AuthState> = {
  [SET_TOKEN](state, accessToken: string) {
    state.token = accessToken;
    setItem(TOKEN, accessToken);
  }
};

const actions: ActionTree<AuthState, RootState> = {
  [LOGIN](state, returnToPath: string) {
    return authService.login(returnToPath);
  },
  [HANDLE_AUTHENTICATION]({ commit }) {
    return authService
      .handleAuthentication()
      .then((authResult: any) => {
        if (authResult && authResult.accessToken) {
          commit(SET_TOKEN, authResult.accessToken);
          return Promise.resolve(authResult);
        }
      })
      .catch((error: any) => Promise.reject(error));
  },
  [LOGOUT]({ commit }) {
    commit(SET_TOKEN, null);
    removeItem(TOKEN);
    authService.logout();
  }
};

const getters: GetterTree<AuthState, RootState> = {
  [TOKEN]: state => state.token,
  [IS_LOGGED_IN]: state => !!state.token && !tokenHasExpired(state.token)
};

const authModule: Module<AuthState, RootState> = {
  namespaced: false,
  state,
  mutations,
  actions,
  getters
};

export default authModule;

export { LOGIN, HANDLE_AUTHENTICATION, IS_LOGGED_IN, LOGOUT };
